import { useEffect, useState } from "react";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { getNodes } from "@utils/getNodes";
import { OrderHistoryDisclaimers } from "@models/user-account-globals";
import { Sanity } from "@models/sanity.model";
import { SubscriptionProvider, useUserProfile } from "@providers/profile";
import { useOrders } from "@providers/order";
import { Shopify } from "@models/shopify.model";
import Loading from "@components/ui/loading";
import PortableText from "@components/portable-text";
import UnfulfilledItems from "./unfulfilled-items";
import {
  AccountHeader,
  AccountSection,
  NoResults,
  OrderList,
  TrackingRow,
  TrackingItems,
  GeneralDisclaimer,
  StyledDisclaimer,
} from "../Account.styled";

const FulfillmentItems = ({ items }: { items: Shopify.Connection<Shopify.FulfillmentItem> }) => {
  const fulfillmentItems = getNodes<Shopify.FulfillmentItem>(items);

  return (
    <div>
      <ul>
        {fulfillmentItems.map((item, idx) => (
          <li key={idx}>
            <p>
              {item.quantity > 1 && `${item.quantity} `}
              {item.lineItem.title}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
};

const FulfillmentTracking = ({ url }: { url: string }) => {
  if (!url) throw new Error(`A URL must be provided.`);

  return (
    <div>
      <strong>
        <a href={url} target="_blank" rel="noreferrer">
          Track Shipment
        </a>
      </strong>
    </div>
  );
};

export const OrderItem = ({ order }: { order: Shopify.Order }) => (
  <dl key={order.id}>
    <dt>Order Number</dt>
    <dd>{order.orderNumber}</dd>
    <dt>Order Date</dt>
    <dd>{format(parseISO(order.processedAt), "MMMM d, yyyy")}</dd>
    <dt>Items</dt>
    <dd>
      <ul>
        {order.successfulFulfillments?.length > 0 &&
          order.successfulFulfillments.map((fulfillment, index) => (
            <TrackingRow key={index}>
              <FulfillmentItems items={fulfillment.fulfillmentLineItems} />
              <TrackingItems>
                {fulfillment.trackingInfo.map((trackingInfo) => (
                  <FulfillmentTracking key={trackingInfo.number} url={trackingInfo.url} />
                ))}
              </TrackingItems>
            </TrackingRow>
          ))}
        <UnfulfilledItems items={order.lineItems} fulfillments={order.successfulFulfillments} />
      </ul>
    </dd>
  </dl>
);

export interface OrderHistoryProps {
  isDigital: boolean;
  digitalDisclaimers?: OrderHistoryDisclaimers;
}

export const OrderHistory = ({ isDigital, digitalDisclaimers }: OrderHistoryProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [orderDisclaimer, setOrderDisclaimer] = useState<Sanity.PortableText>();

  const {
    profileState: { profile, shopifyToken },
  } = useUserProfile();
  const { state: orderState, dispatch, getOrders } = useOrders();
  const orders = Object.keys(orderState.orders).map((key) => orderState.orders[key]);

  useEffect(() => {
    getOrders(shopifyToken, dispatch).finally(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const setDisclaimer = () => {
      const activeSub = profile.subscription_history?.find((sub) => sub.is_active);

      if (activeSub?.provider) {
        setOrderDisclaimer(
          activeSub.provider === SubscriptionProvider.APPLE
            ? digitalDisclaimers?.orders_disclaimer_apple
            : digitalDisclaimers?.orders_disclaimer_google
        );
      }
    };

    if (profile.subscription_history) setDisclaimer();
  }, [profile]);

  return (
    <AccountSection>
      <AccountHeader>
        <h3>Order History</h3>
      </AccountHeader>
      {isDigital && orderDisclaimer && (
        <StyledDisclaimer>
          <PortableText value={orderDisclaimer} />
        </StyledDisclaimer>
      )}
      {isDigital && digitalDisclaimers?.general_disclaimer && (
        <GeneralDisclaimer>{digitalDisclaimers.general_disclaimer}</GeneralDisclaimer>
      )}
      <OrderList>
        {orders?.length > 0 ? (
          orders.map((order) => <OrderItem key={order.id} order={order} />)
        ) : (
          <NoResults>No orders found.</NoResults>
        )}
      </OrderList>
      <Loading visible={loading} />
    </AccountSection>
  );
};

export default OrderHistory;
